import type * as React from 'react';
import { useAppSelector } from '@app/store';
import { selectUserAuthorizationResultsByIds } from '@app/store/userAuthorizationResults';
import type { IFeatureFlag, ITenantType, ITenantTypeName } from '@app/types';
import { tenantTypeSelectors } from '@app/store/tenantTypes';
import { useCompanyQuery } from '@app/queries/company.queries';

import { FEATURE_FLAGS } from '@app/config';

export type IModuleName = 'timesheets' | 'expenses' | 'workloads';

function verifyModule(
  module: IModuleName | undefined,
  enableTimesheets: boolean | null | undefined,
  enableExpenses: boolean | null | undefined,
  enableWorkloads: boolean | null | undefined
) {
  if (!module) {
    return true;
  }
  switch (module) {
    case 'timesheets':
      return enableTimesheets ?? true;
    case 'expenses':
      return enableExpenses ?? true;
    case 'workloads':
      return enableWorkloads ?? true;
  }
}

function verifyFeatureFlag(featureFlag?: IFeatureFlag) {
  if (!featureFlag) {
    return true;
  }
  return FEATURE_FLAGS[featureFlag] ?? false;
}

function verifyTenantType(
  currentTenantType: ITenantType | undefined,
  requiredTenantType: ITenantTypeName | ITenantTypeName[] | undefined
) {
  if (!requiredTenantType) {
    return true;
  }

  const requiredTypes = Array.isArray(requiredTenantType) ? requiredTenantType : [requiredTenantType];

  if (!currentTenantType || !requiredTypes.includes(currentTenantType.name)) {
    return false;
  }

  return true;
}

interface IProps {
  element: React.ReactNode;
  permissions?: string[];
  tenantType?: ITenantTypeName | ITenantTypeName[];
  featureFlag?: IFeatureFlag;
  version?: string;
  module?: IModuleName;
}

export const Restrict = (props: IProps) => {
  const { permissions = [], element, tenantType, featureFlag, version, module } = props;

  const tenantTypeId = useAppSelector((state) => state.auth.user?.currentTenant?.tenantTypeId);
  const currentTenantType = useAppSelector((state) =>
    tenantTypeSelectors.selectById(state.tenantTypes, tenantTypeId ?? '')
  );

  const onPremise = useAppSelector((state) => state.auth.user?.currentTenant?.onPremise);

  const permissionResults = useAppSelector((state) => selectUserAuthorizationResultsByIds(state, permissions));

  const { data: company } = useCompanyQuery();

  const isValidTenantType = verifyTenantType(currentTenantType, tenantType);
  const isAuthorized = permissions.length === 0 || permissionResults.every((result) => result === true);
  const featureEnabled = verifyFeatureFlag(featureFlag);
  const versionEnabled = (onPremise && version === "OnPremise") || (!onPremise && version === "Cloud") || version === undefined ? true : false;
  const moduleEnabled = verifyModule(module, company?.enableTimesheets, company?.enableExpenses, company?.enableWorkloads);

  if (isAuthorized && isValidTenantType && featureEnabled && versionEnabled && moduleEnabled) {
    return element;
  }
  return null;
};
